import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { map, take } from 'rxjs/operators';
import GET_CATEGORIES_BY_ORGANIZATION_ID_SELLERS from './queries/get-categories-by-organization-sellers';
import GET_CATEGORIES_BY_ORGANIZATION_ID_BUYERS from './queries/get-categories-by-organization-buyers';
import GET_CATEGORIZATION_SCHEMES_BY_ORG from './queries/get-categorization-schemes-by-org';
import { ApolloInstances } from '../graphql.module';
import {
  CategorizationScheme,
  Identifier,
  RestParent,
  Spend,
} from '../../../types/types';
import GET_CATEGORIZATION_SCHEME from './queries/get-categorization-scheme';
import GET_CATEGORY from './queries/get-category';
import { PublicCategorizationSchemes } from '@aptitude/shared-vanilla';

export interface Category {
  id: number;
  name: string;
  type: string;
  effectiveDate: number;
  actualSpend?: Spend;
  categorizationScheme: CategorizationScheme;
  parent: RestParent;
  statistic?: Statistic;
}
interface Statistic {
  productCount: number;
}

export enum CategorizationSchemeTypes {
  CUSTOM_CATEGORIZATION_SCHEME = 'Custom Categorization Scheme',
  CATEGORIZATION_SCHEME = 'Categorization Scheme',
  PRIVATE_CATEGORIZATION_SCHEME = 'Private Categorization Scheme',
}
export interface CategorizationSchemeExtended extends CategorizationScheme {
  type: CategorizationSchemeTypes;
  filters?: Array<Filter>;
}
export interface Filter {
  name: string;
  parentType: string;
  values: Array<FilterValue>;
}
export interface FilterValue {
  categorizationSchemeId: number;
  filters?: Array<string>;
  isChecked: boolean;
  isDisabled: boolean;
  id: number;
  name: string;
  type: string;
  count: number;
}

@Injectable({
  providedIn: 'root',
})
export class CategoryGraphqlService {
  constructor(private apollo: Apollo) {}
  /**
   * Fetches a categorization scheme by its ID using a GraphQL query.
   *
   * @param id - The unique identifier of the categorization scheme to fetch.
   * @param useNewPscLabel - Optional flag to determine whether to use the new PSC label format.
   *                         If `true`, modifies the name based on specific conditions.
   *                         Defaults to `false`.
   * @returns An observable that emits the fetched categorization scheme data.
   */
  getCategorizationScheme(id: number, useNewPscLabel = false) {
    return this.apollo
      .use(ApolloInstances.CATEGORY)
      .query<{ category: Identifier }>({
        query: GET_CATEGORIZATION_SCHEME,
        variables: { id },
      })
      .pipe(
        map((request) => {
          if (useNewPscLabel) {
            return {
              ...request.data.category,
              name:
                request.data.category.id === PublicCategorizationSchemes.CC
                  ? 'CC'
                  : request.data.category.name,
            };
          }
          return request.data.category;
        }),
      );
  }

  /**
   * Fetches a category by its ID using a GraphQL query.
   *
   * @param id - The unique identifier of the category to fetch.
   * @param useNewPscLabel - Optional flag to determine whether to use the new PSC label format.
   *                         If `true`, modifies the categorization scheme and parent name based on specific conditions.
   *                         Defaults to `false`.
   * @returns An observable that emits the fetched category data. If `useNewPscLabel` is `true`,
   *          the categorization scheme and parent name may be altered based on their IDs.
   */
  getCategory(id: number, useNewPscLabel = false) {
    return this.apollo
      .use(ApolloInstances.CATEGORY)
      .query<{ category: Category }>({
        query: GET_CATEGORY,
        variables: { id },
      })
      .pipe(
        map((request) => {
          if (useNewPscLabel) {
            return {
              ...request.data.category,
              categorizationScheme: {
                ...request.data.category.categorizationScheme,
                name:
                  request.data.category.categorizationScheme.id ===
                  PublicCategorizationSchemes.CC
                    ? 'CC'
                    : request.data.category.categorizationScheme.name,
              },
              parent: {
                ...request.data.category.parent,
                name:
                  request.data.category.parent.id ===
                  PublicCategorizationSchemes.CC
                    ? 'CC'
                    : request.data.category.parent.name,
              },
            };
          }
          return request.data.category;
        }),
      );
  }

  /**
   * Retrieves categorization schemes associated with an organization.
   * Optionally modifies the name of specific schemes based on the `useNewPscLabel` flag.
   *
   * @param useNewPscLabel - A boolean flag indicating whether to use the new PSC label.
   *                         If `true`, the name of the scheme with ID matching `PublicCategorizationSchemes.CC`
   *                         will be set to 'CC'. Defaults to `false`.
   * @returns An observable that emits an array of categorization schemes, optionally modified based on the flag.
   */
  getCategorizationSchemesByOrg(useNewPscLabel = false) {
    return this.apollo
      .use(ApolloInstances.CATEGORY)
      .query<{
        categorizationSchemesByOrg: Array<CategorizationSchemeExtended>;
      }>({
        query: GET_CATEGORIZATION_SCHEMES_BY_ORG,
      })
      .pipe(
        map((request) => {
          if (useNewPscLabel) {
            return request.data.categorizationSchemesByOrg.map((scheme) => ({
              ...scheme,
              name:
                scheme.id === PublicCategorizationSchemes.CC
                  ? 'CC'
                  : scheme.name,
            }));
          }
          return request.data.categorizationSchemesByOrg;
        }),
      );
  }

  getCategoriesByOrganizationId(organizationId: number, isSeller: boolean) {
    return this.apollo
      .use(ApolloInstances.CATEGORY)
      .query<{
        categoriesByOrganization: Array<Category>;
      }>({
        query: isSeller
          ? GET_CATEGORIES_BY_ORGANIZATION_ID_SELLERS
          : GET_CATEGORIES_BY_ORGANIZATION_ID_BUYERS,
        variables: { organizationId: organizationId },
      })
      .pipe(map((request) => request.data.categoriesByOrganization));
  }
}
